import { track } from '@tldraw/tldraw'
import { BoldProps } from './Types'
import { Bold as Icon } from 'blaze-ui/icons'
import { useUpdateStyle } from '../VisualEditor/hooks/useUpdateStyle'
import { FontWeightStyle } from '../VisualEditor/StyleProps'
import { setProseWeight, useActiveProseMarks } from '../Shapes/TextBoxShape/activeProseView'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { observer } from 'mobx-react-lite'
import { Button } from 'common/Prose/Toolbar/ToolbarBravo/WriterFormatToolbar/Button'
import { useAvailableWeights } from '../VisualEditor/hooks/useAvailableWeights'

const Bold = track(
  observer(({ editor, fontWeightStyle, fontFamilyStyle, italicStyle, tooltipPlacement }: BoldProps) => {
    const updateStyle = useUpdateStyle(editor)
    const proseMarks = useActiveProseMarks()
    const availableWeights = useAvailableWeights(fontFamilyStyle, italicStyle)
    const nonBoldWeight = availableWeights.includes(400) ? 400 : availableWeights[0]

    // Bold requires the font to actually have a 700 weight — true whether we're editing per-run weight
    // or setting the whole-shape weight (otherwise we'd emit a weight the lambda can't render).
    // Outside prose editing, a mixed selection also disables the toggle.
    const isDisabled = !availableWeights.includes(700) || (!proseMarks && fontWeightStyle.type === 'mixed')

    // While editing, bold reflects the selection's per-run weight (>= 700); when no run sets a weight
    // (null), it falls back to the whole-shape weight — so a whole-shape-bold box still reads bold in the
    // editor. 'mixed' reads off. Outside editing, it's just the whole-shape weight.
    const wholeShapeBold = fontWeightStyle.type === 'shared' && fontWeightStyle.value === 700
    const proseWeight = proseMarks?.weight
    let isToggled = wholeShapeBold
    if (typeof proseWeight === 'number') isToggled = proseWeight >= 700
    else if (proseWeight === 'mixed') isToggled = false

    return (
      <Tooltip label="Bold" placement={tooltipPlacement}>
        <Button
          isDisabled={isDisabled}
          variant={isToggled ? 'squareToggleOn' : 'squareToggleOff'}
          onPress={() => {
            const target = isToggled ? nonBoldWeight : 700
            if (!setProseWeight(target)) updateStyle(FontWeightStyle, target)
          }}
          aria-label="Bold"
          icon={() => <Icon size={20} />}
        />
      </Tooltip>
    )
  }),
)

export { Bold }
